import { sleepAsync } from '../libs/promise';
import { instanceOf } from '../libs/classes';

type FileOptions = {
    multiple?: boolean;
    accept?: string;
    capture?: 'user' | 'environment';
};

type Size = {
    width: number;
    height: number;
};

type Point = {
    x: number;
    y: number;
};

type ImageMime =
    | 'image/*'
    | 'image/apng'
    | 'image/avif'
    | 'image/gif'
    | 'image/jpeg'
    | 'image/png'
    | 'image/svg+xml'
    | 'image/webp';

type ResizeConfig = {
    mime: string | ImageMime;
    quality?: number;
};

export default class FileHandler {
    static dataUrl(src: Blob) {
        return new Promise<string>((res) => {
            const reader = new FileReader();
            reader.onload = () => res(reader.result as string);
            reader.readAsDataURL(src);
        });
    }

    static loadDialog(
        options: FileOptions & { multiple: true }
    ): Promise<FileList | null>;
    static loadDialog(
        options?: FileOptions & { multiple?: false }
    ): Promise<File | null>;
    static async loadDialog(options: FileOptions = {}) {
        const input = document.createElement('input');
        input.type = 'file';
        if (options.multiple !== undefined) input.multiple = options.multiple;
        if (options.accept !== undefined) input.accept = options.accept;
        if (options.capture !== undefined)
            input.setAttribute('capture', options.capture);
        const onChange = new Promise((r) =>
            input.addEventListener('change', r)
        );
        const onCancel = new Promise((r) =>
            window.addEventListener('focus', r, { once: true })
        ).then(() => new Promise((r) => setTimeout(r, 500)));
        input.click();
        await Promise.race([onChange, onCancel]);
        return input.files && (options.multiple ? input.files : input.files[0]);
    }

    /** @deprecated use shrinkImage */
    static async resizeImage(file: File, longerSideUpTo = 2000) {
        const resized = await this.shrinkImage(file, longerSideUpTo);
        return this.dataUrl(resized);
    }

    static download(file: Blob | string, filename: string) {
        if (typeof file == 'string') file = new Blob([file]);
        const url = URL.createObjectURL(file);
        try {
            const anchor = document.createElement('a');
            anchor.download = filename;
            anchor.href = url;
            anchor.click();
        } finally {
            sleepAsync(30).then(() => {
                URL.revokeObjectURL(url);
            });
        }
    }

    static readAsText(file: Blob, encoding?: string) {
        return new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.readAsText(file, encoding);
        });
    }

    static async handleAsImage<T>(
        blob: Blob,
        callback: (img: HTMLImageElement) => Promise<T>
    ) {
        const img = new Image();
        try {
            await new Promise((r) => {
                img.onload = r;
                img.src = URL.createObjectURL(blob);
            });
            return await callback(img);
        } finally {
            if (img.src) URL.revokeObjectURL(img.src);
        }
    }

    static async imageSize(src: Blob | CanvasImageSource): Promise<Size> {
        return src instanceof Blob
            ? this.handleAsImage(src, async (img) => this.sourceSize(img))
            : this.sourceSize(src);
    }

    private static sourceSize(src: CanvasImageSource): Size {
        if (src instanceof HTMLVideoElement)
            return { width: src.videoWidth, height: src.videoHeight };
        if (instanceOf(src, 'VideoFrame'))
            return { width: src.displayWidth, height: src.displayHeight };
        return {
            width: Number(src.width),
            height: Number(src.height),
        };
    }

    static async clipImage(
        canvasSize: Size,
        src: CanvasImageSource,
        drawSize: Size,
        drawPoint: Point,
        config?: ResizeConfig
    ): Promise<Blob> {
        const srcSize = this.sourceSize(src);
        const canvas = document.createElement('canvas');
        canvas.width = canvasSize.width;
        canvas.height = canvasSize.height;

        canvas
            .getContext('2d')
            ?.drawImage(
                src,
                0,
                0,
                srcSize.width,
                srcSize.height,
                drawPoint.x,
                drawPoint.y,
                drawSize.width,
                drawSize.height
            );

        const blob = await new Promise<Blob | null>((r) =>
            canvas.toBlob(r, config?.mime as string, config?.quality)
        );
        if (!blob) throw new Error('Failed resize image.');
        return blob;
    }

    private static resizeCenter(
        canvasSize: Size,
        src: CanvasImageSource,
        drawScale: number,
        config?: ResizeConfig
    ) {
        const srcSize = this.sourceSize(src);
        const drawSize = {
            width: drawScale * srcSize.width,
            height: drawScale * srcSize.height,
        };
        const drawPoint = {
            x: (canvasSize.width - drawSize.width) / 2,
            y: (canvasSize.height - drawSize.height) / 2,
        };
        return this.clipImage(canvasSize, src, drawSize, drawPoint, config);
    }

    static resize(
        src: CanvasImageSource | Blob,
        drawScale: number,
        config?: ResizeConfig
    ): Promise<Blob> {
        if (src instanceof Blob) {
            return this.handleAsImage(src, async (img) => {
                return this.resize(img, drawScale, {
                    mime: src.type,
                    ...(config ?? {}),
                });
            });
        }
        const srcSize = this.sourceSize(src);
        const canvasSize = {
            width: drawScale * srcSize.width,
            height: drawScale * srcSize.height,
        };
        return this.resizeCenter(canvasSize, src, drawScale, config);
    }

    static async resizeCover(
        src: Blob | CanvasImageSource,
        canvasSize: Size,
        config?: ResizeConfig
    ): Promise<Blob> {
        if (src instanceof Blob) {
            return this.handleAsImage(src, async (img) => {
                return this.resizeCover(img, canvasSize, {
                    mime: src.type,
                    ...(config ?? {}),
                });
            });
        }
        const srcSize = await this.imageSize(src);
        const drawScale = Math.max(
            canvasSize.width / srcSize.width,
            canvasSize.height / srcSize.height
        );
        return this.resizeCenter(canvasSize, src, drawScale, config);
    }

    static async resizeContain(
        src: Blob | CanvasImageSource,
        canvasSize: Size,
        config?: ResizeConfig
    ): Promise<Blob> {
        if (src instanceof Blob) {
            return this.handleAsImage(src, async (img) => {
                return this.resizeContain(img, canvasSize, {
                    mime: src.type,
                    ...(config ?? {}),
                });
            });
        }
        const srcSize = await this.imageSize(src);
        const drawScale = Math.min(
            canvasSize.width / srcSize.width,
            canvasSize.height / srcSize.height
        );
        return this.resizeCenter(canvasSize, src, drawScale, config);
    }

    static async shrinkImage(
        src: Blob | CanvasImageSource,
        longerSideUpTo: number,
        config?: ResizeConfig
    ) {
        const srcSize = await this.imageSize(src);
        const drawScale = Math.max(
            longerSideUpTo / srcSize.width,
            longerSideUpTo / srcSize.height
        );
        if (drawScale < 1) return this.resize(src, drawScale, config);
        return src instanceof Blob
            ? src
            : this.clipImage(srcSize, src, srcSize, { x: 0, y: 0 }, config);
    }
}
